import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import Loading from 'components/Pages/Wine/View/Loading'
import Content from 'components/Pages/CapturedWine/Content'
import { ProductContext } from 'components/Pages/Wine/View/Main/_methods'
import { CapturedWineContext } from 'components/Pages/CapturedWine/_methods/context'
import { getLocaleId } from 'utils/getLocaleId'
import useNotification from 'utils/notification'
import { usePublicCapturedWineQuery } from 'api/hooks'
import Footer from './Footer'
import Header from './Header'

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  font-family: var(--typography-normal);
  scrollbar-gutter: stable;
`

const PublicCapturedWineView = () => {
  const { id, lang } = useParams<{ id: string; lang: string }>()
  const notify = useNotification()
  const localeId = getLocaleId(lang)

  const { data, loading } = usePublicCapturedWineQuery({
    variables: {
      id: id!,
      localeId,
    },
    fetchPolicy: 'no-cache',
    context: { ignoreAccessErrors: true },
    onError(error) {
      notify(error.message)
    },
  })

  const cw = data?.capturedWine
  const cp = cw?.product
  const cw_wine = cp?.wine

  const product = useMemo(() => {
    if (!cp || !cw_wine) return null

    const rawTechSheet = cw_wine.techSheets?.[0] ?? null
    const techSheet = rawTechSheet
      ? {
          ...rawTechSheet,
          awards: (cw_wine.awards ?? []).map(a => ({
            ...a,
            isUsed: true,
            wineAward: {
              ...a.award,
              year: a.year,
              minScore: a.minScore,
              maxScore: a.maxScore,
              rewards: a.rewards,
            },
          })),
        }
      : null

    const wine = {
      ...cw_wine,
      acceptability: false,
      canOverrideAcceptability: false,
      canOverrideFacts: false,
      verified: false,
      techSheet,
      techSheetEn: undefined,
      vintageList: (cw.vintageList ?? []).map(v => ({
        id: v.id,
        vintageText: v.product?.wine?.vintageText,
        products: [],
      })),
      products: (cw.products ?? []).map(p => ({
        id: p.id,
        slug: null,
        priceAtWinery: null,
        currency: null,
        bottleSize: p.product?.bottleSize,
        packageType: p.product?.packageType,
      })),
    }

    return {
      id: cp.id,
      slug: null,
      acceptability: true,
      canOverrideAcceptability: true,
      canOverrideFacts: false,
      verified: false,
      bottleSize: cp.bottleSize,
      packageType: cp.packageType,
      closureType: null,
      frontLabelImage: cp.frontLabelImage,
      frontLabelImageThumb: cp.frontLabelImageThumb,
      backLabelImage: cp.backLabelImage,
      backLabelImageThumb: cp.backLabelImageThumb,
      bottleImage: cp.bottleImage,
      bottleImageThumb: cp.bottleImageThumb,
      shortUrl: null,
      annualProduction: null,
      bottlingDate: null,
      appellationRatings: [],
      priceAtWinery: null,
      currency: null,
      mainSynonym: null,
      wine,
    } as any
  }, [data])

  return (
    <Wrapper>
      <Header />
      {loading && <Loading />}
      {cw && product && (
        <CapturedWineContext.Provider
          value={{
            capturedWine: cw as any,
            isAuthor: false,
            isTransitional: false,
            authorUserId: null,
            getCapturedWineUrl: id => `/${lang}/public-captured-wine/${id}`,
            canEdit: false,
          }}
        >
          <ProductContext.Provider value={product}>
            <Content />
          </ProductContext.Provider>
          <Footer />
        </CapturedWineContext.Provider>
      )}
    </Wrapper>
  )
}

export default PublicCapturedWineView
